import { Show } from '@/components/admin/show';
import { RecordField } from '@/components/admin/record-field';
import { Separator } from '@/components/ui/separator';
import { Presence } from './Presence';
import { CreationDeRelation } from './CreationDeRelation';
import { GestesSurUnCompte } from './GestesSurUnCompte';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA FICHE D'UN COMPTE — F-59, ET SON OUVERTURE LAISSE UNE TRACE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-59 : « **une fiche de compte existe, et son ouverture laisse une ligne de trace.** Elle
 * porte ce que la ligne ne porte pas : **les autres rattachements**, les **relations
 * extérieures**, les **adresses passées**. »
 *
 * F-55 en fait la borne : « entrer dans la surface et parcourir une liste ne laissent **aucune**
 * ligne de trace. **Seule l'ouverture d'une fiche en laisse une.** » Donc **rien ne précharge
 * cette fiche**, et aucun survol ne l'ouvre : ce serait écrire des lignes que personne n'a
 * demandées.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **DEUX BRÈCHES VOULUES DU CLOISONNEMENT, ET ELLES SONT ASSUMÉES PAR ÉCRIT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-60 : « une structure voit **les autres rattachements et les relations extérieures** de ses
 * personnes, **sur écran comme en export**, et c'est assumé. » Et F-66 le redit de l'export :
 * « l'export des comptes sort **des données que la structure ne possède pas**, et c'est
 * assumé : les autres rattachements et les relations extérieures d'une personne **lui
 * appartiennent à elle**. »
 *
 * Ce ne sont donc pas des fuites, ce sont deux décisions — et le motif de fond est en F-60 :
 * « **un compte appartient à la personne**, une structure l'atteint par le rattachement ».
 * L'écran le dit, pour qu'un `orgadmin` sache qu'il regarde autre chose que sa structure.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE LA FICHE NE PORTE PAS, ET LA LISTE EST DANS LE CAHIER DES CHARGES.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-59, en refus explicite : « **ce qu'aucun des deux rôles ne voit d'une personne** : son
 * **espace de fichiers**, ses **conversations** et tout objet du chat, ses **préférences**. »
 * Et : « **aucune surface ne montre l'empreinte de mot de passe, ni les codes de vérification,
 * ni les liens d'auto-connexion en cours.** »
 *
 * Le contrat ne les porte pas — il n'y a donc **rien à retirer ici**, et c'est le bon montage :
 * une fiche qui recevrait ces champs et choisirait de les taire serait à un `console.log`
 * d'une fuite.
 *
 * **Les connexions en cours n'y sont pas non plus**, et là c'est différent : §6.14 les accorde
 * au `superadmin` — « oui, sans trace, borne acquise » — et **aucune des vingt-deux routes ne
 * rend les connexions d'une AUTRE personne**. Le droit n'a pas de chemin ; voir la note de
 * `connexions` dans `matrice.ts`. Remonté au porteur, pas comblé ici : `admin/` ne crée aucune
 * route.
 */
export function FicheDeCompte() {
  return (
    <Show>
      <div className="flex flex-col gap-6">
        <section className="grid gap-2 sm:grid-cols-2">
          <RecordField source="nom" label="Nom" />
          <RecordField source="prenom" label="Prénom" />
          <RecordField source="email" label="Adresse" />
          <RecordField source="role" label="Rôle" />
          <RecordField
            source="etatDuCompte"
            label="État du compte"
            render={(compte: { etatDuCompte: string }) =>
              compte.etatDuCompte === 'ANONYMIZED' ? 'anonymisé' : 'actif'
            }
          />
          <RecordField
            source="creeLe"
            label="Compte créé le"
            render={(compte: { creeLe: string }) =>
              new Date(compte.creeLe).toLocaleDateString('fr-FR')
            }
          />
          <RecordField
            source="presence"
            label="Présence"
            // **Une personne à la fois, sans trace, et aucun agrégat** — F-59.
            render={(compte: { presence: 'absent' | 'connecte' | 'occupe' }) => (
              <Presence etat={compte.presence} />
            )}
          />
        </section>

        <Separator />

        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">Adresses passées</h3>
          <RecordField
            source="adressesPassees"
            label={false}
            empty="Aucune adresse passée."
            render={(compte: { adressesPassees: string[] }) =>
              compte.adressesPassees.length === 0 ? (
                <span className="text-muted-foreground text-sm">Aucune adresse passée.</span>
              ) : (
                <ul className="list-disc pl-4 text-sm">
                  {compte.adressesPassees.map((adresse) => (
                    <li key={adresse}>{adresse}</li>
                  ))}
                </ul>
              )
            }
          />
        </section>

        <Separator />
        {/* **Le geste de mise en relation est AVANT les gestes de retrait**, et l'ordre a un
            motif : F-68 écrit l'ordre de l'extinction — « défaire les relations d'abord » —,
            donc mettre la création au milieu des gestes qui défont ferait lire cet ordre à
            l'envers. Ici, on ouvre en haut et on ferme en bas. */}
        <CreationDeRelation />

        <Separator />
        <GestesSurUnCompte />
      </div>
    </Show>
  );
}
